import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Search as SearchIcon, GetApp as GetAppIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { DashboardStackCustom } from "components/StackCustom/styles";
import BackdropLoading from "components/BackdropLoading";
import { useAuth } from "shared/hooks/auth";
import { useNavigation } from "contexts/notificationsContext";
import { useNavigate, useParams } from "react-router-dom";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import { debounce } from "lodash";
import {
  obterConfiguracaoConsulta,
  consultarAplicabilidadesSistemista,
  consultarAplicabilidadesFornecedor,
  exportarConsulta,
  listarLinhasSistema,
  listarTipologias,
  IEmpresaConsulta,
  IConfiguracaoConsulta,
  IConsultaAplicabilidade,
  IConsultarAplicabilidadesParams,
  IFSelect,
} from "services/modules/aplicabilidades";
import { BadgeIndicador } from "./components/BadgeIndicador";
import { STATUS_COMPONENTE, COR_DESCONTINUADO } from "constants/statusComponente";

// Q3 (2026-07-06): a Consulta carrega o conjunto inteiro (filtros de dropdown) e pagina/filtra
// no cliente, para que os filtros de coluna do grid atuem sobre o resultado COMPLETO. Limite alto
// o suficiente para cobrir os maiores conjuntos observados (ordem de milhares de linhas).
const LIMITE_TODOS_REGISTROS = 100000;

export const ConsultaAplicabilidades = () => {
  const { user } = useAuth();
  const { openNotification } = useNavigation();
  const navigate = useNavigate();
  const { empresaId } = useParams<{ empresaId: string }>();

  // Estados principais
  const [empresas, setEmpresas] = useState<IEmpresaConsulta[]>([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<IEmpresaConsulta | null>(null);
  const [configuracao, setConfiguracao] = useState<IConfiguracaoConsulta | null>(null);
  const [configuracaoUsuario, setConfiguracaoUsuario] = useState<IConfiguracaoConsulta | null>(null);
  const [tipoVisao, setTipoVisao] = useState<"Sistemista" | "Fornecedor">("Sistemista");
  const [dadosConsulta, setDadosConsulta] = useState<IConsultaAplicabilidade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalRecords, setTotalRecords] = useState(0);
  // Tarefa 1.3.3 (FASE 04): mensagem do backend quando a empresa não possui
  // referências de aplicabilidade configuradas (TelasSistemaReferencias 6/7/8)
  const [mensagemConfiguracao, setMensagemConfiguracao] = useState<string | null>(null);

  // Filtros
  const [filtroEmpresa, setFiltroEmpresa] = useState<number | "">("");
  const [filtroReferencia, setFiltroReferencia] = useState<number | "">("");
  const [filtroLinhaSistema, setFiltroLinhaSistema] = useState<number | "">("");
  const [filtroTipologia, setFiltroTipologia] = useState<number | "">("");
  const [filtroBusca, setFiltroBusca] = useState("");

  // Opções de filtros
  const [filtroProjetista, setFiltroProjetista] = useState<number | "">("");
  const [projetistasOpcoes, setProjetistasOpcoes] = useState<IFSelect[]>([]);
  const [linhasSistemaOpcoes, setLinhasSistemaOpcoes] = useState<IFSelect[]>([]);
  const [tipologiasOpcoes, setTipologiasOpcoes] = useState<IFSelect[]>([]);

  // Filtros por coluna (dinâmicos)
  const [filtrosColunas, setFiltrosColunas] = useState<Record<string, string>>({});

  // Debounce para busca
  const debouncedFiltroBusca = useCallback(
    debounce((value: string) => {
      setFiltroBusca(value);
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  // Carregar empresas disponíveis
  useEffect(() => {
    carregarEmpresas();
  }, []);

  // Carregar configuração quando empresa for selecionada
  useEffect(() => {
    if (empresaSelecionada) {
      carregarConfiguracao();
      carregarLinhasSistema();
      carregarTipologias();
    }
  }, [empresaSelecionada, tipoVisao]);

  // Recarregar projetistas quando configuração do USUÁRIO for carregada
  useEffect(() => {
    if (configuracaoUsuario) {
      carregarProjetistas();
    }
  }, [configuracaoUsuario]);

  // Recarregar linhas quando projetista mudar
  useEffect(() => {
    if (empresaSelecionada) {
      carregarLinhasSistema();
    }
  }, [filtroProjetista]);

  // Recarregar tipologias quando projetista ou linha/sistema mudar
  useEffect(() => {
    if (empresaSelecionada) {
      carregarTipologias();
    }
  }, [filtroProjetista, filtroLinhaSistema]);

  // Q3 (2026-07-06): a consulta agora CARREGA TODAS as linhas do conjunto atual (filtros de
  // dropdown) de uma vez; a paginação e os filtros de coluna do grid passam a ser client-side.
  // Assim os filtros de coluna agem sobre o RESULTADO COMPLETO (antes só a página carregada).
  // page/pageSize saíram das dependências: mudar de página apenas re-fatia no cliente (sem novo
  // fetch). O fetch só refaz quando muda o conjunto (empresa/visão/dropdowns/busca).
  useEffect(() => {
    if (empresaSelecionada) {
      carregarDadosConsulta();
    }
  }, [
    empresaSelecionada,
    tipoVisao,
    filtroReferencia,
    filtroLinhaSistema,
    filtroTipologia,
    filtroProjetista,
    filtroBusca,
  ]);

  // Inicializar empresa do usuário ou da URL
  useEffect(() => {
    if (empresas.length > 0 && !empresaSelecionada) {
      let empresaParaSelecionar: IEmpresaConsulta | undefined;

      // 1. Prioridade: empresaId da URL
      if (empresaId) {
        empresaParaSelecionar = empresas.find((e) => e.idEmpresa === parseInt(empresaId));
      }

      // 2. Prioridade: user.idEmpresa
      if (!empresaParaSelecionar && user?.idEmpresa) {
        empresaParaSelecionar = empresas.find((e) => e.idEmpresa === user.idEmpresa);
      }

      // 3. Prioridade: se houver apenas 1 empresa, selecionar automaticamente
      if (!empresaParaSelecionar && empresas.length === 1) {
        empresaParaSelecionar = empresas[0];
      }

      if (empresaParaSelecionar) {
        setEmpresaSelecionada(empresaParaSelecionar);
        setFiltroEmpresa(empresaParaSelecionar.idEmpresa);
        setTipoVisao(empresaParaSelecionar.visualizacaoPadrao as "Sistemista" | "Fornecedor");
      }
    }
  }, [empresas, user, empresaId, empresaSelecionada]);

  const carregarEmpresas = async () => {
    try {
      const idEmpresaUsuario = user?.idEmpresa;
      if (idEmpresaUsuario) {
        const config = await obterConfiguracaoConsulta(idEmpresaUsuario);
        if (config) {
          setConfiguracaoUsuario(config);
          if (config.empresasDisponiveis && config.empresasDisponiveis.length > 0) {
            setEmpresas(config.empresasDisponiveis);
            return;
          }
        }
        // Plano 07/10/2026 (Frente 1): sem "empresas para consulta", só a própria empresa. A lista
        // de todas as empresas (Empresa/ListarEmpresas) exige a tela Configuracao e a API recusa
        // empresa fora do escopo do token.
        setEmpresas([
          {
            idEmpresa: idEmpresaUsuario,
            nomeFantasia: user?.empresa || "",
            visualizacaoPadrao: config?.visualizacaoPadrao || "Sistemista",
          },
        ]);
        return;
      }
      setEmpresas([]);
    } catch (error) {
      console.error("Erro ao carregar empresas:", error);
      openNotification({
        message: "Erro ao carregar empresas. Tente novamente.",
        type: "error",
      });
    }
  };

  const carregarConfiguracao = async () => {
    if (!empresaSelecionada) return;
    try {
      const config = await obterConfiguracaoConsulta(empresaSelecionada.idEmpresa);
      if (config) {
        setConfiguracao(config);
        // Atualizar tipo de visão se não foi definido ainda
        if (!tipoVisao) {
          setTipoVisao(config.visualizacaoPadrao as "Sistemista" | "Fornecedor");
        }
      }
    } catch (error) {
      console.error("Erro ao carregar configuração:", error);
    }
  };

  const carregarProjetistas = async () => {
    try {
      if (configuracaoUsuario?.projetistasConfigurados && configuracaoUsuario.projetistasConfigurados.length > 0) {
        const projetistasConfig: IFSelect[] = configuracaoUsuario.projetistasConfigurados.map((p) => ({
          value: p.idProjetista,
          label: p.nome,
        }));
        setProjetistasOpcoes(projetistasConfig);
        return;
      }
      // Frente 6 (07/09/2026): sem fallback pelas Referências. O servidor já devolve os
      // projetistas liberados para a empresa ou, sem liberação, todos os do cadastro.
      setProjetistasOpcoes([]);
    } catch (error) {
      console.error("Erro ao carregar projetistas:", error);
    }
  };

  const carregarLinhasSistema = async () => {
    try {
      const projetistaId = filtroProjetista ? Number(filtroProjetista) : undefined;
      // Q2 (2026-07-06): na Consulta, sem projetista selecionado, o dropdown de Linha mostra apenas
      // as linhas dos projetistas liberados p/ a empresa (3º arg = true). Cadastro não usa a flag.
      const linhas = await listarLinhasSistema(projetistaId, empresaSelecionada?.idEmpresa, true);
      setLinhasSistemaOpcoes(linhas);
      if (filtroLinhaSistema && !linhas.some(l => l.value === filtroLinhaSistema)) {
        setFiltroLinhaSistema("");
      }
    } catch (error) {
      console.error("Erro ao carregar linhas/sistemas:", error);
    }
  };

  const carregarTipologias = async () => {
    try {
      const tipologias = await listarTipologias(
        filtroProjetista ? Number(filtroProjetista) : undefined,
        filtroLinhaSistema ? Number(filtroLinhaSistema) : undefined
      );
      setTipologiasOpcoes(tipologias || []);
      // Limpar tipologia selecionada quando mudar projetista ou linha/sistema
      if (filtroProjetista || filtroLinhaSistema) {
      setFiltroTipologia("");
      }
    } catch (error) {
      console.error("Erro ao carregar tipologias:", error);
      setTipologiasOpcoes([]);
    }
  };

  const carregarDadosConsulta = async () => {
    if (!empresaSelecionada) {
      setDadosConsulta([]);
      setTotalRecords(0);
      setMensagemConfiguracao(null);
      return;
    }

    setIsLoading(true);
    try {
      const params: IConsultarAplicabilidadesParams = {
        idEmpresa: empresaSelecionada.idEmpresa,
        // Q3: carrega o conjunto inteiro (paginação client-side). page=1 + limite alto.
        page: 1,
        limit: LIMITE_TODOS_REGISTROS,
        idReferencia: filtroReferencia ? Number(filtroReferencia) : undefined,
        idLinhaSistema: filtroLinhaSistema ? Number(filtroLinhaSistema) : undefined,
        idTipologia: filtroTipologia ? Number(filtroTipologia) : undefined,
        idProjetista: filtroProjetista ? Number(filtroProjetista) : undefined,
        busca: filtroBusca || undefined,
      };

      const resultado =
        tipoVisao === "Sistemista"
          ? await consultarAplicabilidadesSistemista(params)
          : await consultarAplicabilidadesFornecedor(params);

      setDadosConsulta(resultado.items || []);
      setTotalRecords(resultado.totalRecords || 0);
      // Q3: conjunto recarregado → volta para a primeira página (paginação client-side).
      setPage(1);
      // Tarefa 1.3.3 (FASE 04): o backend envia mensagemConfiguracao quando a empresa
      // não possui referências configuradas (campo ainda não tipado no service).
      setMensagemConfiguracao((resultado as any).mensagemConfiguracao || null);
    } catch (error) {
      console.error("Erro ao carregar dados da consulta:", error);
      setDadosConsulta([]);
      setTotalRecords(0);
      setMensagemConfiguracao(null);
      openNotification({
        message: "Erro ao carregar aplicabilidades. Tente novamente.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportar = async () => {
    if (!empresaSelecionada) {
      openNotification({
        message: "Selecione uma empresa para exportar.",
        type: "warning",
      });
      return;
    }

    setIsExporting(true);
    try {
      const params: IConsultarAplicabilidadesParams = {
        idEmpresa: empresaSelecionada.idEmpresa,
        idReferencia: filtroReferencia ? Number(filtroReferencia) : undefined,
        idLinhaSistema: filtroLinhaSistema ? Number(filtroLinhaSistema) : undefined,
        idTipologia: filtroTipologia ? Number(filtroTipologia) : undefined,
        idProjetista: filtroProjetista ? Number(filtroProjetista) : undefined,
        busca: filtroBusca || undefined,
      };

      await exportarConsulta(params, tipoVisao);
      openNotification({
        message: "Arquivo exportado com sucesso!",
        type: "success",
      });
    } catch (error) {
      console.error("Erro ao exportar:", error);
      openNotification({
        message: "Erro ao exportar dados. Tente novamente.",
        type: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Filtrar dados por colunas
  const dadosFiltrados = useMemo(() => {
    let filtrados = dadosConsulta;

    // Aplicar filtros por coluna
    Object.entries(filtrosColunas).forEach(([coluna, valor]) => {
      if (!valor) return;

      filtrados = filtrados.filter((item) => {
        if (coluna === "codigoBase" || coluna === "aplicavel") {
          return item.codigoPrincipal.toLowerCase().includes(valor.toLowerCase());
        }
        if (coluna === "referencia") {
          if (valor.length < 2) return true;
          return item.referenciaPrincipal.toLowerCase().includes(valor.toLowerCase());
        }
        // Filtros dinâmicos por referência
        if (coluna.startsWith("aplicavel_") || coluna.startsWith("codigoBase_") || coluna.startsWith("ref_")) {
          const ref = coluna.split("_").slice(1).join("_");
          const aplicabilidade = item.aplicabilidades.find((a) => a.referencia === ref);
          // CORREÇÃO 2026-07-06: linha SEM código nesta coluna não pode casar com o valor
          // digitado no filtro da coluna — deve ser OCULTADA (antes retornava true, deixando
          // passar todas as linhas com a coluna vazia, que são a maioria nas colunas esparsas).
          if (!aplicabilidade || aplicabilidade.codigos.length === 0) return false;
          return aplicabilidade.codigos.some((c) =>
            c.codigo.toLowerCase().includes(valor.toLowerCase())
          );
        }
        return true;
      });
    });

    return filtrados;
  }, [dadosConsulta, filtrosColunas]);

  // Q3 (2026-07-06): paginação CLIENT-SIDE sobre o resultado já filtrado por coluna.
  // dadosFiltrados agora cobre o conjunto COMPLETO (todas as linhas carregadas). A página exibida
  // é uma fatia; total e nº de páginas derivam do total filtrado. paginaAtual é "clampada" para
  // nunca apontar além do disponível (evita página em branco após um filtro reduzir o conjunto).
  const totalFiltrado = dadosFiltrados.length;
  const totalPaginasCliente = Math.max(1, Math.ceil(totalFiltrado / pageSize));
  const paginaAtualCliente = Math.min(page, totalPaginasCliente);
  const registrosPagina = useMemo(
    () => dadosFiltrados.slice((paginaAtualCliente - 1) * pageSize, paginaAtualCliente * pageSize),
    [dadosFiltrados, paginaAtualCliente, pageSize]
  );

  // Q3: ao mudar qualquer filtro de coluna, volta para a primeira página (mostra resultados do
  // início em vez de manter uma página que pode não existir mais após o filtro).
  useEffect(() => {
    setPage(1);
  }, [filtrosColunas]);

  // Usuário "amarrado" a uma empresa (user.idEmpresa setado) nunca pode trocar de empresa;
  // o dropdown só faz sentido para perfis administrativos (sem idEmpresa) com múltiplas empresas
  // visíveis. Reverte regressão causada pelo fallback `listarEmpresasConsulta()` quando a config
  // do usuário não retorna empresasDisponiveis.
  const mostrarFiltroEmpresa = !user?.idEmpresa && empresas.length > 1;
  // Mostrar filtro de referência sempre (desabilitado se houver apenas 1)
  const mostrarFiltroReferencia = configuracao && configuracao.referenciasDisponiveis.length > 0;

  return (
    <DashboardStackCustom>
      <BackdropLoading isOpen={isLoading} />
      {/* FASE 03 (Pergunta 5 — 03-07): indicador "% Aplicabilidade Realizadas" removido da tela
          de Consulta por decisão do PO. */}
      <NavigationHeader
        title="CONSULTA DE APLICABILIDADES"
        label="Home > Consulta"
        hasBackButton={true}
        goBack={() => navigate("/dashboardMof")}
      />

      <Paper elevation={3}>
        {/* Filtros */}
        <Box sx={{ p: 2, bgcolor: "#f5f5f5" }}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* Filtro Empresa — visível apenas quando há 2+ empresas */}
            {mostrarFiltroEmpresa && (
              <Grid item xs={12} sm={6} md>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "primary.main",
                      fontSize: "0.75rem",
                      mb: 0.5,
                      fontWeight: 500,
                    }}
                  >
                    Empresa (Fantasia)
                  </Typography>
                  <MuiSelect
                    value={filtroEmpresa || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        setFiltroEmpresa("");
                        setEmpresaSelecionada(null);
                        setTipoVisao("Sistemista");
                      } else {
                        const id = value as number;
                      setFiltroEmpresa(id);
                      const empresa = empresas.find((e) => e.idEmpresa === id);
                      if (empresa) {
                        setEmpresaSelecionada(empresa);
                        setTipoVisao(empresa.visualizacaoPadrao as "Sistemista" | "Fornecedor");
                        }
                      }
                    }}
                    displayEmpty
                    disabled={false}
                    sx={{
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "40px",
                      width: "100%",
                      "& .MuiSelect-select": {
                        py: 1.25,
                        px: 1.5,
                        color: "#212121",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        bgcolor: "#e0e0e0",
                      },
                      "&:hover": {
                        bgcolor: "#e0e0e0",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#616161",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "#e0e0e0 !important",
                        "& .MuiSelect-select": {
                          color: "#212121",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#616161",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {empresas && empresas.length > 0 ? (
                      empresas.map((emp) => (
                      <MenuItem key={emp.idEmpresa} value={emp.idEmpresa}>
                        {emp.nomeFantasia}
                      </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled>
                        Nenhuma empresa disponível
                      </MenuItem>
                    )}
                  </MuiSelect>
                </FormControl>
              </Grid>
            )}

            {/* Filtro Sistemista */}
            <Grid item xs={12} sm={6} md>
              <FormControl fullWidth>
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontSize: "0.75rem",
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  Sistemista
                </Typography>
                <MuiSelect
                  value={filtroProjetista}
                  onChange={(e) => {
                    setFiltroProjetista(e.target.value as number | "");
                    setFiltroLinhaSistema("");
                    setFiltroTipologia("");
                    setPage(1);
                  }}
                  displayEmpty
                  disabled={false}
                  sx={{
                    bgcolor: "#e0e0e0",
                    borderRadius: "4px",
                    height: "40px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      py: 1.25,
                      px: 1.5,
                      color: "#212121",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused": {
                      bgcolor: "#e0e0e0",
                    },
                    "&:hover": {
                      bgcolor: "#e0e0e0",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#616161",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#e0e0e0 !important",
                      "& .MuiSelect-select": {
                        color: "#212121",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#616161",
                      },
                    },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {projetistasOpcoes.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Grid>

            {/* Filtro Linha/Sistema */}
            <Grid item xs={12} sm={6} md>
              <FormControl fullWidth>
                <Typography
                  variant="caption"
                  sx={{
                    color: "primary.main",
                    fontSize: "0.75rem",
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  Linha/Sistema
                </Typography>
                <MuiSelect
                  value={filtroLinhaSistema}
                  onChange={(e) => {
                    setFiltroLinhaSistema(e.target.value as number | "");
                    setFiltroTipologia("");
                    setPage(1);
                  }}
                  displayEmpty
                  sx={{
                    bgcolor: "#e0e0e0",
                    borderRadius: "4px",
                    height: "40px",
                    width: "100%",
                    "& .MuiSelect-select": {
                      py: 1.25,
                      px: 1.5,
                      color: "#212121",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused": {
                      bgcolor: "#e0e0e0",
                    },
                    "&:hover": {
                      bgcolor: "#e0e0e0",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#616161",
                    },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {linhasSistemaOpcoes.map((linha) => (
                    <MenuItem key={linha.value} value={linha.value}>
                      {linha.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
            </Grid>

            {/* Filtro Referência */}
              <Grid item xs={12} sm={6} md>
                <FormControl fullWidth>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "primary.main",
                      fontSize: "0.75rem",
                      mb: 0.5,
                      fontWeight: 500,
                    }}
                  >
                    {tipoVisao === "Sistemista"
                      ? "Referência Códigos Aplicáveis"
                      : "Referência Base"}
                  </Typography>
                  <MuiSelect
                    value={filtroReferencia}
                    onChange={(e) => {
                      setFiltroReferencia(e.target.value as number | "");
                      setPage(1);
                    }}
                    displayEmpty
                  disabled={false}
                    sx={{
                      bgcolor: "#e0e0e0",
                      borderRadius: "4px",
                      height: "40px",
                      width: "100%",
                      "& .MuiSelect-select": {
                        py: 1.25,
                        px: 1.5,
                        color: "#212121",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "&.Mui-focused": {
                        bgcolor: "#e0e0e0",
                      },
                      "&:hover": {
                        bgcolor: "#e0e0e0",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#616161",
                      },
                      "&.Mui-disabled": {
                        bgcolor: "#e0e0e0 !important",
                        "& .MuiSelect-select": {
                          color: "#212121",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#616161",
                        },
                      },
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {configuracao?.referenciasDisponiveis?.map((ref) => (
                      <MenuItem key={ref.idReferencia} value={ref.idReferencia}>
                        {ref.descricao}
                      </MenuItem>
                    )) || []}
                  </MuiSelect>
                </FormControl>
              </Grid>

            </Grid>

            {/* P-15.1: Exportar sempre visível na Consulta — exporta a visão atualmente exibida
                (Sistemista ou Fornecedor) com os filtros aplicados. PO §6 e técnico §5 não restringem
                Exportar por visualizacaoPadrao. */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
                onClick={handleExportar}
                disabled={!empresaSelecionada || isLoading || isExporting}
              >
                {isExporting ? "EXPORTANDO..." : "EXPORTAR"}
              </Button>
            </Box>
        </Box>

        {/* Tabela Customizada */}
        <Box sx={{ p: 3 }}>
          <Typography
            variant="body2"
            sx={{ color: COR_DESCONTINUADO, display: "block", mb: 1, fontWeight: 500 }}
          >
            * Códigos de componentes na cor vermelha foram descontinuados pelo fornecedor
          </Typography>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              {(() => {
                // Referências (colunas) devem vir da configuração para manter ORDEM/QUANTIDADE fixas (como no mock).
                // Se não houver configuração, cai no fallback pelos dados.
                const referenciasUnicas: string[] = (() => {
                  const refsConfig = (configuracao?.referenciasDisponiveis ?? [])
                    .map((r) => (r.descricao ?? "").trim())
                    .filter(Boolean);

                  if (refsConfig.length > 0) return refsConfig;

                  const refs: string[] = [];
                  const refsSet = new Set<string>();
                  dadosConsulta.forEach((item) => {
                    item.aplicabilidades.forEach((app) => {
                      const nome = (app.referencia ?? "").trim();
                      if (!nome) return;
                      if (!refsSet.has(nome)) {
                        refsSet.add(nome);
                        refs.push(nome);
                      }
                    });
                  });
                  return refs;
                })();

                // Estilos do layout conforme mock (azul só no header)
                const HEADER_TOP_BG = "#FFFFFF";
                const HEADER_TOP_TEXT = "#333333";
                const HEADER_DARK = "#3C90AD";
                const HEADER_LIGHT = "#7FB9C9";
                const FILTER_BG = "#BDBDBD";
                const BORDER = "#D0D0D0";
                const ROW_ODD = "#FFFFFF";
                const ROW_EVEN = "#F3F3F3";

                return (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <thead>
                      {/* LINHA 1: Headers principais - um para cada coluna */}
                      <tr>
                        {tipoVisao === "Sistemista" ? (
                          <>
                            {/* Sistemista: CÓDIGO BASE (2 colunas) + APLICÁVEL (repetido para cada referência) */}
                            <th
                              colSpan={2}
                              style={{
                                backgroundColor: HEADER_TOP_BG,
                                color: HEADER_TOP_TEXT,
                                padding: "10px 8px",
                                textAlign: "center",
                                borderBottom: `1px solid ${BORDER}`,
                                borderRight: `1px solid ${BORDER}`,
                                fontWeight: 700,
                                fontSize: "13px",
                              }}
                            >
                              CÓDIGO BASE
                            </th>
                            {referenciasUnicas.map((ref) => (
                              <th
                                key={`header-${ref}`}
                                style={{
                                  backgroundColor: HEADER_TOP_BG,
                                  color: HEADER_TOP_TEXT,
                                  padding: "10px 8px",
                                  textAlign: "center",
                                  borderBottom: `1px solid ${BORDER}`,
                                  borderRight: `1px solid ${BORDER}`,
                                  fontWeight: 700,
                                  fontSize: "13px",
                                }}
                              >
                                APLICÁVEL
                              </th>
                            ))}
                          </>
                        ) : (
                          <>
                            {/* Fornecedor: APLICÁVEL (1 coluna) + CODIGO BASE (repetido para cada referência) */}
                            <th
                              style={{
                                backgroundColor: HEADER_TOP_BG,
                                color: HEADER_TOP_TEXT,
                                padding: "10px 8px",
                                textAlign: "center",
                                borderBottom: `1px solid ${BORDER}`,
                                borderRight: `1px solid ${BORDER}`,
                                fontWeight: 700,
                                fontSize: "13px",
                              }}
                            >
                              APLICÁVEL
                            </th>
                            {referenciasUnicas.map((ref) => (
                              <th
                                key={`header-${ref}`}
                                style={{
                                  backgroundColor: HEADER_TOP_BG,
                                  color: HEADER_TOP_TEXT,
                                  padding: "10px 8px",
                                  textAlign: "center",
                                  borderBottom: `1px solid ${BORDER}`,
                                  borderRight: `1px solid ${BORDER}`,
                                  fontWeight: 700,
                                  fontSize: "13px",
                                }}
                              >
                                CODIGO BASE
                              </th>
                            ))}
                          </>
                        )}
                      </tr>

                      {/* LINHA 2: Sub-headers com nomes das colunas/referências */}
                      <tr>
                        {tipoVisao === "Sistemista" ? (
                          <>
                            <th
                              style={{
                                backgroundColor: HEADER_DARK,
                                color: "white",
                                padding: "10px 8px",
                                textAlign: "center",
                                border: `1px solid ${BORDER}`,
                                fontWeight: 700,
                                fontSize: "12px",
                                minWidth: "200px",
                              }}
                            >
                              CÓDIGO
                            </th>
                            <th
                              style={{
                                backgroundColor: HEADER_DARK,
                                color: "white",
                                padding: "10px 8px",
                                textAlign: "center",
                                border: `1px solid ${BORDER}`,
                                fontWeight: 700,
                                fontSize: "12px",
                                minWidth: "120px",
                              }}
                            >
                              REFERÊNCIA
                            </th>
                          </>
                        ) : (
                          <th
                            style={{
                              backgroundColor: HEADER_DARK,
                              color: "white",
                              padding: "10px 8px",
                              textAlign: "center",
                              border: `1px solid ${BORDER}`,
                              fontWeight: 700,
                              fontSize: "12px",
                              minWidth: "150px",
                            }}
                          >
                            {/* No mock, aparece o nome do fornecedor (igual a empresa selecionada) */}
                            {empresaSelecionada?.nomeFantasia || "APLICÁVEL"}
                          </th>
                        )}
                        {referenciasUnicas.map((ref) => (
                          <th
                            key={ref}
                            style={{
                              backgroundColor: HEADER_LIGHT,
                              color: "white",
                              padding: "10px 8px",
                              textAlign: "center",
                              border: `1px solid ${BORDER}`,
                              fontWeight: 700,
                              fontSize: "12px",
                              minWidth: "180px",
                            }}
                          >
                            {ref}
                          </th>
                        ))}
                      </tr>

                      {/* LINHA 3: Filtros */}
                      <tr>
                        {tipoVisao === "Sistemista" ? (
                          <>
                            <th
                              style={{
                                backgroundColor: FILTER_BG,
                                padding: "6px 8px",
                                border: `1px solid ${BORDER}`,
                              }}
                            >
                              <input
                                type="text"
                                placeholder="FILTRO"
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  border: "none",
                                  outline: "none",
                                  backgroundColor: "transparent",
                                  fontSize: "12px",
                                  textAlign: "center",
                                  color: "#333",
                                }}
                                onChange={(e) => {
                                  setFiltrosColunas((prev) => ({ ...prev, codigoBase: e.target.value }));
                                }}
                              />
                            </th>
                            <th
                              style={{
                                backgroundColor: FILTER_BG,
                                padding: "6px 8px",
                                border: `1px solid ${BORDER}`,
                              }}
                            >
                              <input
                                type="text"
                                placeholder="FILTRO"
                                style={{
                                  width: "100%",
                                  padding: "6px 8px",
                                  border: "none",
                                  outline: "none",
                                  backgroundColor: "transparent",
                                  fontSize: "12px",
                                  textAlign: "center",
                                  color: "#333",
                                }}
                                onChange={(e) => {
                                  setFiltrosColunas((prev) => ({ ...prev, referencia: e.target.value }));
                                }}
                              />
                            </th>
                          </>
                        ) : (
                          <th
                            style={{
                              backgroundColor: FILTER_BG,
                              padding: "6px 8px",
                              border: `1px solid ${BORDER}`,
                            }}
                          >
                            <input
                              type="text"
                              placeholder="FILTRO"
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                border: "none",
                                outline: "none",
                                backgroundColor: "transparent",
                                fontSize: "12px",
                                textAlign: "center",
                                color: "#333",
                              }}
                              onChange={(e) => {
                                setFiltrosColunas((prev) => ({ ...prev, aplicavel: e.target.value }));
                              }}
                            />
                          </th>
                        )}
                        {referenciasUnicas.map((ref) => (
                          <th
                            key={`filter-${ref}`}
                            style={{
                              backgroundColor: FILTER_BG,
                              padding: "6px 8px",
                              border: `1px solid ${BORDER}`,
                            }}
                          >
                            <input
                              type="text"
                              placeholder="FILTRO"
                              style={{
                                width: "100%",
                                padding: "6px 8px",
                                border: "none",
                                outline: "none",
                                backgroundColor: "transparent",
                                fontSize: "12px",
                                textAlign: "center",
                                color: "#333",
                              }}
                              onChange={(e) => {
                                setFiltrosColunas((prev) => ({
                                  ...prev,
                                  [`ref_${ref}`]: e.target.value,
                                }));
                              }}
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>

                    {/* CORPO DA TABELA */}
                    <tbody>
                      {dadosFiltrados.length === 0 ? (
                        <tr>
                          <td
                            colSpan={tipoVisao === "Sistemista" ? 2 + referenciasUnicas.length : 1 + referenciasUnicas.length}
                            style={{ textAlign: "center", padding: "32px", color: "#999", fontSize: "14px" }}
                          >
                            {/* Tarefa 1.3.3 (FASE 04): distinguir "empresa sem configuração de
                                referências" (mensagem do backend ou configuração local vazia)
                                de simplesmente "sem dados" */}
                            {totalRecords === 0 &&
                            (mensagemConfiguracao ||
                              (configuracao?.referenciasDisponiveis?.length ?? 0) === 0)
                              ? "Empresa sem referências de aplicabilidade configuradas — verifique Cadastro de Empresas > Aplicabilidades"
                              : "Nenhum registro encontrado"}
                          </td>
                        </tr>
                      ) : (
                        registrosPagina
                          .map((row, rowIndex) => {
                          const bgColor = rowIndex % 2 === 0 ? ROW_ODD : ROW_EVEN;
                          return (
                            <tr key={`${row.codigoPrincipal}-${row.referenciaPrincipal}-${rowIndex}`}>
                              {/* Colunas fixas (CÓDIGO BASE para Sistemista, APLICÁVEL para Fornecedor) */}
                              {tipoVisao === "Sistemista" ? (
                                <>
                                  {/* Coluna CÓDIGO - fundo normal (FASE 06 P-02a: truncar com ellipsis + Tooltip) */}
                                  <td
                                    style={{
                                      backgroundColor: bgColor,
                                      color: row.idStatusPrincipal === STATUS_COMPONENTE.DESCONTINUADO ? COR_DESCONTINUADO : "#111",
                                      padding: "8px",
                                      border: `1px solid ${BORDER}`,
                                      fontWeight: 700,
                                      fontSize: "12px",
                                      verticalAlign: "middle",
                                      maxWidth: 200,
                                    }}
                                  >
                                    {/* FASE 03 (23-06): exibir ícone de agrupamento também na coluna fixa
                                        CÓDIGO BASE, igual às colunas dinâmicas APLICÁVEL (row.temAgrupamento /
                                        row.codigosAgrupados já vêm do backend). */}
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <Tooltip title={row.codigoPrincipal ?? ""} arrow placement="top">
                                        <span
                                          style={{
                                            display: "inline-block",
                                            maxWidth: "100%",
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            verticalAlign: "middle",
                                          }}
                                        >
                                          {row.codigoPrincipal}
                                        </span>
                                      </Tooltip>
                                      {row.temAgrupamento && (row.codigosAgrupados?.length ?? 0) > 0 && (
                                        <BadgeIndicador
                                          tipo="agrupamento"
                                          tamanho="small"
                                          tooltip={`Agrupamento: ${row.codigosAgrupados!.join(", ")}`}
                                          descontinuado={row.idStatusPrincipal === STATUS_COMPONENTE.DESCONTINUADO}
                                        />
                                      )}
                                    </Box>
                                  </td>
                                  {/* Coluna REFERÊNCIA - fundo normal e centralizada */}
                                  <td
                                    style={{
                                      backgroundColor: bgColor,
                                      color: "#333",
                                      padding: "8px",
                                      border: `1px solid ${BORDER}`,
                                      fontSize: "12px",
                                      verticalAlign: "middle",
                                      textAlign: "center",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {row.referenciaPrincipal}
                                  </td>
                                </>
                              ) : (
                                /* Modo Fornecedor - Coluna APLICÁVEL com fundo normal (FASE 06 P-02a: truncar + Tooltip) */
                                <td
                                  style={{
                                    backgroundColor: bgColor,
                                    color: row.idStatusPrincipal === STATUS_COMPONENTE.DESCONTINUADO ? COR_DESCONTINUADO : "#111",
                                    padding: "8px",
                                    border: `1px solid ${BORDER}`,
                                    fontWeight: 700,
                                    fontSize: "12px",
                                    verticalAlign: "middle",
                                    maxWidth: 200,
                                  }}
                                >
                                  {/* FASE 03 (23-06): ícone de agrupamento na coluna fixa APLICÁVEL
                                      (visão Fornecedor), simétrico à visão Sistemista. */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Tooltip title={row.codigoPrincipal ?? ""} arrow placement="top">
                                      <span
                                        style={{
                                          display: "inline-block",
                                          maxWidth: "100%",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          verticalAlign: "middle",
                                        }}
                                      >
                                        {row.codigoPrincipal}
                                      </span>
                                    </Tooltip>
                                    {row.temAgrupamento && (row.codigosAgrupados?.length ?? 0) > 0 && (
                                      <BadgeIndicador
                                        tipo="agrupamento"
                                        tamanho="small"
                                        tooltip={`Agrupamento: ${row.codigosAgrupados!.join(", ")}`}
                                        descontinuado={row.idStatusPrincipal === STATUS_COMPONENTE.DESCONTINUADO}
                                      />
                                    )}
                                  </Box>
                                </td>
                              )}

                              {/* Colunas dinâmicas (APLICÁVEL para Sistemista, CODIGO BASE para Fornecedor) */}
                              {referenciasUnicas.map((ref) => {
                                const aplicabilidade = row.aplicabilidades.find((a) => a.referencia === ref);
                                const codigos = aplicabilidade?.codigos ?? [];
                                return (
                                  <td
                                    key={ref}
                                    style={{
                                      backgroundColor: bgColor,
                                      padding: "8px",
                                      border: `1px solid ${BORDER}`,
                                      fontSize: "12px",
                                      verticalAlign: "top",
                                    }}
                                  >
                                    {codigos.length === 0 ? null : (
                                      /* FASE 03 (ponto 7 — 23-06): empilhar os códigos (um por linha). Quando o código tem
                                         agrupamento, exibir código + ícone à direita (tooltip lista os agrupados). Substitui o
                                         colapso FASE 06 P-02b (N>1 → só ícone), que escondia os códigos. */
                                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                        {codigos.map((c, i) => {
                                          const codigoVisivel = c.codigo && c.codigo.trim().length > 0
                                            ? c.codigo
                                            : (c.codigosAgrupados && c.codigosAgrupados.length > 0 ? c.codigosAgrupados[0] : "");
                                          const descontinuado = c.idStatus === STATUS_COMPONENTE.DESCONTINUADO;
                                          return (
                                            <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                              <Tooltip title={codigoVisivel} arrow placement="top">
                                                <span
                                                  style={{
                                                    color: descontinuado ? COR_DESCONTINUADO : "#222",
                                                    display: "inline-block",
                                                    maxWidth: "180px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    verticalAlign: "middle",
                                                  }}
                                                >
                                                  {codigoVisivel}
                                                </span>
                                              </Tooltip>
                                              {c.ehAgrupamento && (c.codigosAgrupados?.length ?? 0) > 0 && (
                                                <BadgeIndicador
                                                  tipo="agrupamento"
                                                  tamanho="small"
                                                  tooltip={`Agrupamento: ${c.codigosAgrupados!.join(", ")}`}
                                                  descontinuado={descontinuado}
                                                />
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                          })
                      )}
                    </tbody>
                  </table>
                );
              })()}
              {/* Paginação — Q3: totais e navegação derivam do resultado filtrado (client-side) */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total: {totalFiltrado} registros
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={paginaAtualCliente <= 1}
                    onClick={() => setPage(paginaAtualCliente - 1)}
                  >
                    Anterior
                  </Button>
                  <Typography variant="body2">
                    Página {paginaAtualCliente} de {totalPaginasCliente}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={paginaAtualCliente >= totalPaginasCliente}
                    onClick={() => setPage(paginaAtualCliente + 1)}
                  >
                    Próxima
                  </Button>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <MuiSelect
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                    >
                      <MenuItem value={12}>12</MenuItem>
                      <MenuItem value={24}>24</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </MuiSelect>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </DashboardStackCustom>
  );
};

